import type { Environment } from "@/types/config";

/**
 * Origin whitelist for PostMessage security
 * Only messages from these origins will be processed
 */
export class OriginValidator {
  private static readonly ALLOWED_ORIGINS: Record<Environment, string[]> = {
    prod: [
      "https://search.caredove.com",
      "https://assessment.caredove.com",
      "https://form.caredove.com",
    ],
    dev: [
      "https://search.dev.caredove.com",
      "https://assessment.dev.caredove.com",
      "https://form.caredove.dev",
      "https://local.caredove.dev:3000",
      "http://localhost:3000",
      "http://localhost:4001",
      "http://127.0.0.1:3000",
      "http://127.0.0.1:4001",
    ],
    stage: [
      "https://search.stage.caredove.com",
      "https://assessment.stage.caredove.com",
      "https://form.caredove.io",
    ],
  };

  /**
   * Domain patterns for flexible matching
   */
  private static readonly DOMAIN_PATTERNS: RegExp[] = [
    /^https:\/\/.*\.caredove\.com$/,
    /^https:\/\/.*\.caredove\.dev$/,
    /^https:\/\/.*\.caredove\.io$/,
  ];

  /**
   * Check if an origin is allowed to send messages
   * @param origin - The origin to validate
   * @param environment - The current environment
   * @returns true if origin is allowed
   */
  static isAllowed(origin: string, environment: Environment = "prod"): boolean {
    // Check exact match first (fast path)
    const allowedList = this.ALLOWED_ORIGINS[environment];
    if (allowedList.includes(origin)) {
      return true;
    }

    // Check pattern matching for dynamic subdomains
    if (environment === "prod") {
      return this.DOMAIN_PATTERNS.some((pattern) => pattern.test(origin));
    }

    // Dev environment allows localhost patterns
    if (environment === "dev") {
      const localhostPattern = /^http:\/\/(localhost|127\.0\.0\.1):\d+$/;
      if (localhostPattern.test(origin)) {
        return true;
      }
      return this.DOMAIN_PATTERNS.some((pattern) => pattern.test(origin));
    }

    // Stage environment
    if (environment === "stage") {
      return this.DOMAIN_PATTERNS.some((pattern) => pattern.test(origin));
    }

    return false;
  }

  /**
   * Get all allowed origins for an environment
   * Useful for debugging and CSP headers
   */
  static getAllowedOrigins(environment: Environment = "prod"): string[] {
    return [...this.ALLOWED_ORIGINS[environment]];
  }

  /**
   * Validate origin and throw if invalid
   * @throws Error if origin is not allowed
   */
  static validateOrThrow(
    origin: string,
    environment: Environment = "prod"
  ): void {
    if (!this.isAllowed(origin, environment)) {
      throw new Error(
        `[CaredoveWidget] Blocked message from untrusted origin: ${origin}`
      );
    }
  }
}
